import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { connectionRows, IssuesDocument, type IssueRow } from "#app/lib/data";

/**
 * Live project rows for an explicit id list — the data layer behind any surface
 * that stores bare project links (an Account's or a Contact's linked Projects)
 * and needs each one's current stage, assignees and age.
 *
 * One query per id list, read through the generic `Issues` operation, so Apollo
 * normalization keeps these rows in step with every board and list mutation.
 */
export function useLinkedIssueSummaries(issueIds: string[]) {
  const variables = useMemo(
    () => ({
      where: { issueId: { in: issueIds } },
      limit: Math.max(issueIds.length, 50),
    }),
    [issueIds],
  );

  const { data, previousData, loading } = useQuery(IssuesDocument, {
    variables,
    skip: issueIds.length === 0,
  });

  // The variables ARE the id list, so linking or unlinking re-keys the query
  // and `data` reads undefined for the round trip. Bridge that gap with
  // `previousData` and intersect with the authoritative ids, so the list never
  // blinks empty mid-mutation and never shows a project that was just removed
  // (`use-favorite-issues.ts` is the same shape).
  const issues = useMemo<IssueRow[]>(() => {
    if (issueIds.length === 0) return [];
    const ids = new Set(issueIds);
    const rows = connectionRows((data ?? previousData)?.issues) ?? [];
    return rows.filter((issue) => ids.has(issue.issueId));
  }, [data, previousData, issueIds]);

  const issueById = useMemo(
    () => new Map(issues.map((issue) => [issue.issueId, issue])),
    [issues],
  );

  return {
    issues,
    issueById,
    initialLoading: loading && !data && !previousData,
  };
}
